/**
 * Derivações PURAS da aba Erros (auditoria de ocorrências erro/warning da esteira)
 * — mesmo padrão dos demais utils da página: tudo que dá para decidir sem JSX mora
 * aqui, com data/hora sempre parametrizável (`agora`) para os testes serem
 * determinísticos.
 *
 * Fonte: `GET /orquestrador/auditoria/erros` (proxy 1:1 do mon — filtros validados
 * lá, janela com teto de 31 dias que a resposta denuncia via `janela_clampada`).
 */

import type { IAuditoriaErrosParams, IAuditoriaErrosResposta } from '../api';
import { isoDia } from './periodoProcessos';

// ── Período (presets 24h/7d/31d + intervalo custom) ──────────────────────────

export type PresetAuditoria = '24h' | '7d' | '31d';

/** Datas date-only (YYYY-MM-DD) para os inputs; `preset` null = editado a mão. */
export interface PeriodoAuditoria {
  preset: PresetAuditoria | null;
  de: string;
  ate: string;
}

const HORAS_DO_PRESET: Record<PresetAuditoria, number> = { '24h': 24, '7d': 168, '31d': 744 };

/**
 * Range de EXIBIÇÃO dos inputs quando um preset é escolhido (dias, aproximação
 * visual — a query do preset usa horas exatas via `paramsDoPeriodoAuditoria`).
 * 31d encosta de propósito no teto do servidor (_MAX_DIAS_JANELA = 31).
 */
export const rangeDoPresetAuditoria = (
  preset: PresetAuditoria,
  agora = new Date(),
): PeriodoAuditoria => ({
  preset,
  de: isoDia(new Date(agora.getTime() - HORAS_DO_PRESET[preset] * 3600000)),
  ate: isoDia(agora),
});

/**
 * Converte o período nos params `de`/`ate` do endpoint:
 * - preset ativo → `de` = agora menos as horas EXATAS do preset, sem `ate`
 *   (o servidor lê "até agora" — é o que "últimas 24h" significa);
 * - intervalo custom → dia inteiro nas duas pontas (00:00 → 23:59:59.999);
 * - intervalo incompleto (um input limpo) → SEM filtro, o servidor usa a janela
 *   padrão dele (7 dias) — nunca uma ponta solta (mesma regra do `paraParams`).
 */
export const paramsDoPeriodoAuditoria = (
  p: PeriodoAuditoria,
  agora = new Date(),
): Pick<IAuditoriaErrosParams, 'de' | 'ate'> => {
  if (p.preset) {
    return { de: new Date(agora.getTime() - HORAS_DO_PRESET[p.preset] * 3600000).toISOString() };
  }
  if (!p.de || !p.ate) return {};
  return {
    de: new Date(`${p.de}T00:00:00`).toISOString(),
    ate: new Date(`${p.ate}T23:59:59.999`).toISOString(),
  };
};

// ── Formatação de células ────────────────────────────────────────────────────

/**
 * "dd/MM HH:mm:ss" lendo os CAMPOS literais do ISO (contrato: `ts` já chega em
 * horário de São Paulo — passar por `new Date()` re-converteria pro fuso do
 * navegador e mentiria a hora). Sem match (null, formato inesperado) → "—".
 */
export const formatarTsAuditoria = (ts: string | null | undefined): string => {
  const m = (ts ?? '').match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
  if (!m) return '—';
  return `${m[3]}/${m[2]} ${m[4]}:${m[5]}:${m[6]}`;
};

/** "dd/MM/yyyy" com a mesma leitura literal (aviso de janela clampada). */
export const formatarDiaAuditoria = (iso: string | null | undefined): string => {
  const m = (iso ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '—';
  return `${m[3]}/${m[2]}/${m[1]}`;
};

/**
 * Mensagem da linha: sufixa "…" quando o SERVIDOR cortou no armazenamento
 * (`mensagem_truncada`) e a mensagem ainda não termina em reticências; vazia → "—".
 */
export const mensagemDaLinha = (mensagem: string, truncada: boolean): string => {
  if (!mensagem) return '—';
  if (truncada && !mensagem.endsWith('…')) return `${mensagem}…`;
  return mensagem;
};

/**
 * Célula Etapa: exibe a canônica (fallback no cru quando o DE-PARA não conheceu a
 * grafia); o `title` denuncia o que o bot mandou cru quando difere do exibido.
 */
export const celulaEtapa = (
  etapa: string | null,
  etapaRaw: string | null,
): { texto: string; title?: string } => {
  const texto = etapa ?? etapaRaw ?? '—';
  const title = etapaRaw && etapaRaw !== texto ? `Como o bot enviou: ${etapaRaw}` : undefined;
  return { texto, title };
};

/**
 * Chip de CLASSIFICAÇÃO da linha (21/09): o que aparece na coluna entre a etapa
 * e a mensagem. `null` = nada a classificar (a célula mostra "—").
 *
 * O CÓDIGO do catálogo vence o `tipo_erro` quando existe. Os dois classificam a
 * ocorrência, mas com qualidade diferente: `error_nome` ("SEM_PROCURACAO") vem
 * do catálogo fechado e só o fecho de unidade carrega; `tipo_erro` é DERIVADO
 * por regex sobre a mensagem no servidor — acha a classe de exceção quando há
 * uma ("TimeoutError"), e senão cai num prefixo de 60 caracteres da primeira
 * linha. Num motivo de negócio ("Sem procuracao: SEM_PROCURACAO: ATENÇÃO: …")
 * não há classe de exceção, então o prefixo truncado é o que sobraria — pior
 * que o nome exato que já veio pronto ao lado.
 *
 * Função pura e exportada de propósito: a escolha tem três saídas e é a regra
 * que o teste precisa prender sem montar a tela inteira.
 */
export const classificacaoDaLinha = (item: {
  error_nome: string | null;
  error_code: number | null;
  tipo_erro: string | null;
}): { texto: string; title: string } | null => {
  if (item.error_nome) {
    return {
      texto: item.error_nome,
      title: `${item.error_nome} (código ${item.error_code})`,
    };
  }
  if (item.tipo_erro) return { texto: item.tipo_erro, title: item.tipo_erro };
  return null;
};

/**
 * Aviso discreto quando o `de` pedido furou o teto de 31 dias do servidor
 * (`janela_clampada`) — o clamp nunca pode ser silencioso; null = sem aviso.
 */
export const avisoJanelaClampada = (
  resposta: Pick<IAuditoriaErrosResposta, 'janela_clampada' | 'de_efetivo'>,
): string | null => {
  if (!resposta.janela_clampada) return null;
  return `janela limitada a 31 dias — dados desde ${formatarDiaAuditoria(resposta.de_efetivo)}`;
};

// ── Paginação (server-side: total/limit/offset da resposta) ──────────────────

export interface PaginacaoAuditoria {
  paginaAtual: number;
  totalPaginas: number;
  /** 1-based; 0 quando não há itens (o rodapé mostra "0 de 0"). */
  inicio: number;
  fim: number;
  temAnterior: boolean;
  temProxima: boolean;
}

export const paginacaoAuditoria = (
  total: number,
  limit: number,
  offset: number,
): PaginacaoAuditoria => {
  const porPagina = Math.max(1, limit);
  const totalPaginas = Math.max(1, Math.ceil(total / porPagina));
  const paginaAtual = Math.min(totalPaginas, Math.floor(offset / porPagina) + 1);
  return {
    paginaAtual,
    totalPaginas,
    inicio: total === 0 ? 0 : Math.min(total, offset + 1),
    fim: Math.min(total, offset + porPagina),
    temAnterior: offset > 0 && total > 0,
    temProxima: offset + porPagina < total,
  };
};

// ── Rodapé: unidades explícitas (empresas distintas × sem empresa) ───────────

/**
 * Sufixo do rodapé que traduz OCORRÊNCIAS em EMPRESAS (achados 4 e 5 da auditoria
 * 09/09 — a raiz da queixa "a aba Erros diz 108 e o nó de Processos diz 35").
 *
 * Duas correções de unidade num só lugar:
 * - EMPRESAS DISTINTAS: uma mesma empresa aparece N vezes quando o bot retentou,
 *   então o total de ocorrências nunca é comparável ao total de empresas travadas;
 * - SEM EMPRESA: ocorrência com `codi_emp` null (falha de login do Domínio,
 *   fail-safe do PyAutoGUI) entra no total mas some de QUALQUER agregação por
 *   empresa. Ficava invisível, e era exatamente parte da diferença entre os dois
 *   números.
 *
 * ESCOPO do que é contado — decidido POR CAMPO, e o texto diz qual é, sem exceção:
 * - `resumo.empresas_distintas` presente → aquele número é do FILTRO INTEIRO e a
 *   frase omite "nesta página"; ausente → conta a PÁGINA e diz "nesta página";
 * - `resumo.sem_empresa` decide o SEU escopo do mesmo jeito, independentemente.
 *
 * O escopo é por campo porque os dois campos são OPCIONAIS de forma INDEPENDENTE
 * (api.ts: entram no backend em paralelo). Num deploy parcial — só um dos dois no
 * ar — um escopo único para os dois números apresentaria o número da PÁGINA com o
 * rótulo do filtro, que é exatamente a classe de mentira que esta branch corrige.
 * Nunca se apresenta número de página como se fosse do filtro.
 *
 * O ZERO do servidor é dito ("0 sem empresa"): quando o campo veio, "nenhuma
 * ocorrência sem empresa" é uma AFIRMAÇÃO, não ausência de dado — e calar o zero
 * deixaria o rodapé mudo enquanto a tabela pode exibir a linha "sem empresa"
 * (ErrosTab), dois elementos da mesma tela discordando. No fallback por página,
 * ao contrário, o zero não é afirmável sobre o filtro: aí sim é omitido.
 */
export interface SufixoRodapeErros {
  /** Trecho pronto para concatenar ao "Mostrando X–Y de N ocorrências…". */
  texto: string;
  /**
   * Escopo REAL de cada número, um a um — estado misto (deploy parcial) não cabe
   * num booleano só, que era o defeito do `doFiltroInteiro` anterior.
   */
  escopo: {
    empresasDistintas: EscopoContagem;
    semEmpresa: EscopoContagem;
  };
}

/** De onde veio um número do rodapé: o filtro inteiro (servidor) ou só a página. */
export type EscopoContagem = 'filtro' | 'pagina';

export const sufixoRodapeErros = (
  resposta: Pick<IAuditoriaErrosResposta, 'itens' | 'resumo'>,
): SufixoRodapeErros => {
  const distintasServidor = resposta.resumo?.empresas_distintas;
  const semEmpresaServidor = resposta.resumo?.sem_empresa;

  const escopoDistintas: EscopoContagem =
    typeof distintasServidor === 'number' ? 'filtro' : 'pagina';
  const escopoSemEmpresa: EscopoContagem =
    typeof semEmpresaServidor === 'number' ? 'filtro' : 'pagina';

  const distintas =
    escopoDistintas === 'filtro'
      ? (distintasServidor as number)
      : new Set(resposta.itens.filter(it => it.codi_emp).map(it => it.codi_emp as string)).size;
  const semEmpresa =
    escopoSemEmpresa === 'filtro'
      ? (semEmpresaServidor as number)
      : resposta.itens.filter(it => !it.codi_emp).length;

  const rotulo = (e: EscopoContagem) => (e === 'filtro' ? '' : ' nesta página');
  const partes: string[] = [];
  if (distintas > 0 || escopoDistintas === 'filtro') {
    partes.push(
      `${distintas} ${distintas === 1 ? 'empresa distinta' : 'empresas distintas'}${rotulo(escopoDistintas)}`,
    );
  }
  if (semEmpresa > 0 || escopoSemEmpresa === 'filtro') {
    partes.push(`${semEmpresa} sem empresa${rotulo(escopoSemEmpresa)}`);
  }
  return {
    texto: partes.length ? ` (${partes.join(', ')})` : '',
    escopo: { empresasDistintas: escopoDistintas, semEmpresa: escopoSemEmpresa },
  };
};

// ── Headline da aba (§6.1 da spec: calculada, nunca literal) ─────────────────

/**
 * Janela EFETIVA da aba, em texto curto para caber entre parênteses na headline
 * (achado 2/3 da auditoria de incongruências, 09/09). A aba Erros tem período
 * PRÓPRIO (24h/7d/31d ou intervalo livre), independente do seletor global da
 * página — e ainda pode ser encurtada pelo clamp de 31 dias do servidor. Sem
 * dizer a janela, o "N ocorrências no recorte atual" parecia falar do mesmo
 * período das outras abas (que é de COMPETÊNCIA, não de log).
 *
 * A fonte é o `de_efetivo`/`ate_efetivo` que o SERVIDOR devolve — nunca o estado
 * dos inputs: é o único número que descreve o que de fato foi consultado depois
 * do clamp. `ate_efetivo` null significa "até agora" (o caso dos presets).
 *
 * Retorna null quando a janela não é descritível com honestidade (`de_efetivo`
 * ilegível, ou no FUTURO por skew de relógio servidor×navegador) — a headline
 * prefere ficar sem o parêntese a afirmar um período que não existe.
 */
export const janelaEfetiva = (
  resposta: Pick<IAuditoriaErrosResposta, 'de_efetivo' | 'ate_efetivo'>,
  agora = new Date(),
): string | null => {
  const de = new Date(resposta.de_efetivo);
  if (Number.isNaN(de.getTime())) return null;
  if (resposta.ate_efetivo) {
    // Intervalo explícito → os dois dias literais, na mesma leitura do aviso de
    // clamp (`formatarDiaAuditoria` lê os campos do ISO, sem reconverter fuso).
    return `${formatarDiaAuditoria(resposta.de_efetivo)} a ${formatarDiaAuditoria(resposta.ate_efetivo)}`;
  }
  // Preset (sem `ate`): descreve em horas/dias corridos até agora.
  const horasReais = (agora.getTime() - de.getTime()) / 3600000;

  // `de_efetivo` no FUTURO (skew entre o relógio do servidor e o do navegador):
  // não existe janela negativa e não dá para descrever esse período com honestidade
  // — melhor a headline ficar sem o parêntese do que afirmar "últimas 0 horas".
  if (horasReais < 0) return null;

  // O RAMO é decidido pela duração REAL, antes de qualquer arredondamento: com o
  // round primeiro, 47h36m virava horas=48 e caía em dias → "últimos 2 dias" para
  // uma janela de menos de dois dias.
  if (horasReais < 48) {
    // Janela mais curta que uma hora ainda é um período real (a consulta cobre
    // algo) — só não é descritível em horas inteiras sem inventar precisão.
    if (horasReais < 1) return 'menos de 1 hora';
    // Arredonda para a hora mais próxima — a diferença de milissegundos entre
    // montar a query e ler a resposta não pode encurtar o preset.
    const horas = Math.round(horasReais);
    return horas === 1 ? 'última 1 hora' : `últimas ${horas} horas`;
  }
  const dias = Math.round(horasReais / 24);
  return `últimos ${dias} dias`;
};

/** O que a aba sobe pro index para headline/contador — derivado da resposta. */
export interface DadosHeadlineErros {
  total: number;
  topTipo: string | null;
  /** Já em nome de EXIBIÇÃO (apelido resolvido pela aba). */
  topBot: string | null;
  /**
   * Janela efetiva já em texto (`janelaEfetiva`) — a aba resolve, porque só ela
   * tem a resposta do servidor. Ausente/null = headline sem o parêntese (nunca
   * inventar uma janela que não veio).
   */
  janela?: string | null;
}

export const headlineErros = (dados: DadosHeadlineErros | null): string => {
  if (!dados) {
    return 'Cada ocorrência de erro ou warning da esteira: qual bot quebrou, em qual empresa, em que etapa e quando.';
  }
  // A janela entra colada ao número em TODOS os casos (inclusive o zero): "nenhuma
  // ocorrência" sem dizer de quando é a afirmação mais fácil de ler errado da tela.
  const janela = dados.janela ? ` (${dados.janela})` : '';
  if (!dados.total) {
    return `Nenhuma ocorrência no recorte atual${janela} — a esteira passou limpa.`;
  }
  const plural = dados.total === 1 ? 'ocorrência' : 'ocorrências';
  const partes: string[] = [];
  if (dados.topTipo) partes.push(`tipo mais frequente: ${dados.topTipo}`);
  if (dados.topBot) partes.push(`bot mais afetado: ${dados.topBot}`);
  return `${dados.total} ${plural}${janela} no recorte atual${partes.length ? ` — ${partes.join(' · ')}` : ''}.`;
};

// ── Catálogo de etapas ───────────────────────────────────────────────────────

/**
 * Espelho do catálogo FECHADO de `step` do mon (`catalogos.py`, contrato 03/09
 * §1.4 — 8 valores; etapa nova entra por PR lá e AQUI). Select em vez de texto
 * livre: o filtro `etapa` fora do catálogo leva 400 do servidor.
 */
export const ETAPAS_AUDITORIA: readonly string[] = [
  'preparacao',
  'login',
  'selecao_empresa',
  'coleta',
  'processamento',
  'validacao',
  'exportacao',
  'finalizacao',
];
